/**
 * Public pricing & plan selection · Version 1.0 catalogue (six prices).
 * Rendered at /pricing (no session required). The catalogue is static and
 * server-published; money never originates in the browser — checkout sends a
 * lookup_key only. When billing is contained (BILLING_ENABLED=0) the CTA
 * stays visible but disabled with an honest "not yet open" explanation.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { isIOS } from "@/lib/platform";
import NativeSubscriptionScreen from "@/components/mobile/NativeSubscriptionScreen";

const backend = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = [
  { code: "cad", label: "CAD · C$", symbol: "C$", region: "Canada" },
  { code: "usd", label: "USD · US$", symbol: "US$", region: "United States" },
  { code: "gbp", label: "GBP · £", symbol: "£", region: "United Kingdom" },
];
const INCLUDED = [
  "Voyage Home, Journey, Travelers, Dining, Excursions and Finance for every cruise",
  "Weekly Briefing and Route Map intelligence",
  "Ship Guide and partner reference content where licensed",
  "Family sharing and the mobile companion",
];

export const money = (amountMinor, currency) => {
  const c = CURRENCIES.find((x) => x.code === currency) || { symbol: "" };
  return `${c.symbol}${(amountMinor / 100).toFixed(2)}`;
};

export default function PricingPage() {
  // iOS shell: App Store billing only — the Stripe surface is never rendered.
  if (isIOS()) return <NativeSubscriptionScreen />;
  return <WebPricingPage />;
}

function WebPricingPage() {
  const [cat, setCat] = useState(null);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState("usd");
  const [interval, setInterval_] = useState("year");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    fetch(`${backend}/api/billing/catalogue`, { credentials: "include" })
      .then(async (r) => { if (!r.ok) throw new Error(String(r.status)); setCat(await r.json()); })
      .catch(() => setError("The plan catalogue could not be loaded. Please try again shortly."));
  }, []);

  const price = useMemo(
    () => cat?.prices?.find((p) => p.currency === currency && p.interval === interval) || null,
    [cat, currency, interval],
  );
  const monthly = cat?.prices?.find((p) => p.currency === currency && p.interval === "month");
  const annual = cat?.prices?.find((p) => p.currency === currency && p.interval === "year");
  const savingPct = monthly && annual ? Math.round((1 - annual.amount_minor / (monthly.amount_minor * 12)) * 100) : null;
  const enabled = !!cat?.billing_enabled;

  const subscribe = async () => {
    if (!price || !enabled || busy) return;
    setBusy(true); setNotice(null);
    try {
      const r = await fetch(`${backend}/api/payments/checkout`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lookup_key: price.lookup_key, origin_url: window.location.origin }),
      });
      if (r.status === 401 || r.status === 403) {
        try { window.sessionStorage.setItem("cc-pricing-intent", price.lookup_key); } catch (_e) { /* ignore */ }
        setNotice("Sign in or create your account first — you'll come back to this plan.");
        window.setTimeout(() => { window.location.href = "/"; }, 1200);
        return;
      }
      if (r.status === 503) { setNotice("Subscriptions are not open yet. Nothing has been charged."); return; }
      if (!r.ok) { setNotice("Checkout could not be started. Nothing has been charged."); return; }
      const d = await r.json();
      if (d.checkout_url) window.location.href = d.checkout_url;
    } catch (_e) {
      setNotice("Checkout could not be started. Nothing has been charged.");
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#071e3e] text-white" data-testid="cc-pricing-page">
      <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8 lg:py-16">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d4a853]">Cruise Command Membership</p>
        <h1 className="mt-3 max-w-2xl text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: "Archivo, sans-serif" }}>
          One membership. Every voyage, planned once.
        </h1>
        <p className="mt-4 max-w-xl text-base text-white/75 sm:text-lg">
          Choose your currency and billing interval. Prices are shown in the currency you select and renew automatically until you cancel.
        </p>

        {error && <p className="mt-8 rounded-xl border border-red-300/40 bg-red-500/10 p-4 text-sm" data-testid="cc-pricing-error">{error}</p>}
        {!cat && !error && <p className="mt-8 flex items-center gap-2 text-sm text-white/60" data-testid="cc-pricing-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading plans…</p>}

        {cat && (
          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_380px]">
            <section>
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Currency">
                {CURRENCIES.map((c) => (
                  <button key={c.code} type="button" onClick={() => setCurrency(c.code)} aria-pressed={currency === c.code}
                    data-testid={`cc-pricing-currency-${c.code}`}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${currency === c.code ? "border-[#d4a853] bg-[#d4a853] text-[#071e3e]" : "border-white/25 text-white/80 hover:border-white/60"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2" role="group" aria-label="Billing interval">
                {[["month", "Monthly"], ["year", "Annual"]].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setInterval_(k)} aria-pressed={interval === k}
                    data-testid={`cc-pricing-interval-${k}`}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${interval === k ? "border-white bg-white text-[#071e3e]" : "border-white/25 text-white/80 hover:border-white/60"}`}>
                    {label}{k === "year" && savingPct ? ` · save ${savingPct}%` : ""}
                  </button>
                ))}
              </div>
              <ul className="mt-8 space-y-3 text-[15px] text-white/85">
                {INCLUDED.map((line) => (
                  <li key={line} className="flex items-start gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#d4a853]" />{line}</li>
                ))}
              </ul>
            </section>

            <aside className="rounded-2xl border border-white/15 bg-white/[0.06] p-6 backdrop-blur-md" data-testid="cc-pricing-plan-card">
              {price && (
                <>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">{interval === "year" ? "Annual plan" : "Monthly plan"}</p>
                  <p className="mt-2 text-4xl font-semibold" data-testid="cc-pricing-amount">
                    {money(price.amount_minor, price.currency)}
                    <span className="ml-1 text-base font-normal text-white/60">{price.currency.toUpperCase()} / {interval === "year" ? "year" : "month"}</span>
                  </p>
                  {interval === "year" && monthly && (
                    <p className="mt-1 text-sm text-white/60">Equivalent to {money(Math.round(price.amount_minor / 12), price.currency)} per month, billed once a year.</p>
                  )}
                  <button type="button" onClick={subscribe} disabled={!enabled || busy}
                    aria-disabled={!enabled || busy} data-testid="cc-pricing-subscribe"
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#d4a853] px-6 py-3 text-[15px] font-bold text-[#071e3e] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {enabled ? "Subscribe" : "Subscriptions open soon"}
                  </button>
                  {!enabled && (
                    <p className="mt-3 text-[13px] text-white/70" data-testid="cc-pricing-disabled-note">
                      Checkout is not open yet. Nothing can be charged from this page today.
                    </p>
                  )}
                  {notice && <p className="mt-3 text-[13px] text-amber-200" data-testid="cc-pricing-notice">{notice}</p>}
                  <dl className="mt-6 space-y-2 border-t border-white/10 pt-4 text-[12.5px] leading-snug text-white/70" data-testid="cc-pricing-disclosures">
                    <div><dt className="font-semibold text-white/85">Currency</dt><dd>{cat.disclosures.currency}</dd></div>
                    <div><dt className="font-semibold text-white/85">Billing interval</dt><dd>Billed every {interval === "year" ? "12 months" : "month"} on the date you subscribe.</dd></div>
                    <div><dt className="font-semibold text-white/85">Renewal</dt><dd>{cat.disclosures.renewal}</dd></div>
                    <div><dt className="font-semibold text-white/85">Cancellation</dt><dd>{cat.disclosures.cancellation}</dd></div>
                    <div><dt className="font-semibold text-white/85">Trial</dt><dd>{cat.disclosures.trial}</dd></div>
                    <div><dt className="font-semibold text-white/85">Taxes</dt><dd>{cat.disclosures.tax}</dd></div>
                  </dl>
                  <p className="mt-4 flex items-start gap-2 text-[12px] text-white/55"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />Payments are processed by Stripe. Card details never touch Cruise Command servers.</p>
                </>
              )}
            </aside>
          </div>
        )}

        <footer className="mt-14 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-6 text-[12px] text-white/55" data-testid="cc-pricing-footer">
          <span>{cat?.legal_entity || "1001714184 ONTARIO INC. operating as Cruise Command"}</span>
          <a href="/legal/terms.html" className="hover:text-white" data-testid="cc-pricing-terms">Terms of Service</a>
          <a href="/legal/refunds.html" className="hover:text-white" data-testid="cc-pricing-refunds">Cancellation &amp; refunds</a>
          <a href="/legal/privacy.html" className="hover:text-white" data-testid="cc-pricing-privacy">Privacy Policy</a>
          <a href="/" className="hover:text-white" data-testid="cc-pricing-back">Back to Cruise Command</a>
        </footer>
      </div>
    </main>
  );
}
